/**
 * DialogoModificaStrumento — cambiare prezzi e regole di uno strumento a canone dalla console (atlante cap. 251,
 * tappa 4).
 *
 * Prima di confermare dice, in parole, cosa succederà: quali prezzi nascono su Stripe, chi resta alle condizioni di
 * prima. Le regole di validità sono le stesse del server (`pianoDellaModifica`), così un valore sbagliato si vede subito
 * e il server non lo riceve nemmeno. Il salvataggio passa da `consoleModificaStrumento`, che crea i prezzi e rilegge.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, InputAdornment,
  Radio, RadioGroup, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { tokens } from '../../theme';
import type { ComportamentoAlTetto, StrumentoACanone } from '@shared/strumenti/listino';
import { pianoDellaModifica, type ErroreModifica } from '@shared/strumenti/modificaListino';
import { modificaStrumento, type EsitoModificaStrumento } from '../../services/strumentiService';
import { AL_TETTO, alUnita, euro, euroUnita, unita } from './testiStrumenti';

const ERRORI: Record<ErroreModifica, string> = {
  canone_non_valido: 'Il canone va da 0 a 10.000 €, al centesimo.',
  quota_non_valida: 'La quota compresa è un numero intero fra 0 e 1.000.000.',
  extra_non_valido: 'Il prezzo dell’extra va da 0 a 100 €, con al massimo quattro decimali.',
  al_tetto_non_valido: 'Scegli cosa fa lo strumento al tetto.',
  tetto_predefinito_non_valido: 'Il tetto di partenza va da 0 a 10.000 €; vuoto = pari al canone.',
  senza_consumo: 'Questo strumento si paga solo col canone: non ha quota né extra.',
  niente_da_cambiare: 'Niente da salvare: i valori sono quelli di oggi.',
};

/** «0,155» o «0.155» → 0.155; vuoto o storto → NaN (lo dice la validazione). */
function numeroDa(testo: string): number {
  const t = testo.trim().replace(/\s/g, '').replace(',', '.');
  return t === '' ? Number.NaN : Number(t);
}
const testoDa = (n: number) => String(n).replace('.', ',');

export interface DialogoModificaStrumentoProps {
  aperto: boolean;
  onChiudi: () => void;
  onSalvato: (esito: EsitoModificaStrumento) => void;
  strumento: StrumentoACanone;
  /** Le aziende che hanno lo strumento nell'abbonamento: restano alle condizioni di prima. */
  abbonate: number;
}

export const DialogoModificaStrumento: React.FC<DialogoModificaStrumentoProps> = ({ aperto, onChiudi, onSalvato, strumento, abbonate }) => {
  const [canone, setCanone] = useState('');
  const [quota, setQuota] = useState('');
  const [extra, setExtra] = useState('');
  const [inVendita, setInVendita] = useState(true);
  const [alTetto, setAlTetto] = useState<ComportamentoAlTetto>('solo_avviso');
  /** Vuoto = il tetto di partenza è il canone (null nel listino). */
  const [tettoPartenza, setTettoPartenza] = useState('');
  const [salvo, setSalvo] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  /** Solo canone (`consumo: null`): niente quota, extra né tetto, solo il canone e la vendita. */
  const consumoListino = strumento.consumo;
  const senzaConsumo = consumoListino === null;

  useEffect(() => {
    if (!aperto) return;
    setCanone(testoDa(strumento.canone_mensile_eur));
    setQuota(strumento.consumo ? String(strumento.consumo.quota_compresa) : '');
    setExtra(strumento.consumo ? testoDa(strumento.consumo.prezzo_extra_eur) : '');
    setInVendita(strumento.in_vendita);
    setAlTetto(strumento.al_tetto);
    setTettoPartenza(strumento.tetto_predefinito_eur === null ? '' : testoDa(strumento.tetto_predefinito_eur));
    setErrore(null);
  }, [aperto, strumento]);

  const u = consumoListino?.unita ?? 'minuti';
  const tettoPartenzaEur = tettoPartenza.trim() === '' ? null : numeroDa(tettoPartenza);
  const richiesta = {
    strumento: strumento.capability,
    canone_mensile_eur: numeroDa(canone),
    ...(senzaConsumo ? {} : { quota_compresa: numeroDa(quota), prezzo_extra_eur: numeroDa(extra) }),
    in_vendita: inVendita,
    al_tetto: alTetto,
    tetto_predefinito_eur: tettoPartenzaEur,
  };
  // Un calcolo puro e piccolo: si rifà a ogni tasto, così dice subito cosa non va.
  const esito = pianoDellaModifica(strumento, richiesta);

  const conseguenze: string[] = [];
  if (esito.ok) {
    const p = esito.piano;
    if (p.nuovo_canone) conseguenze.push(`Su Stripe nasce un prezzo nuovo per il canone: da ${euro(strumento.canone_mensile_eur)} a ${euro(p.canone_mensile_eur)} al mese.`);
    if (p.nuovo_consumo && p.consumo && consumoListino) {
      conseguenze.push(
        `Su Stripe nasce un prezzo nuovo per il consumo: ${p.consumo.quota_compresa} ${unita(u, p.consumo.quota_compresa)} compresi `
        + `(erano ${consumoListino.quota_compresa}), poi ${euroUnita(p.consumo.prezzo_extra_eur)} l’uno (era ${euroUnita(consumoListino.prezzo_extra_eur)}).`,
      );
    }
    if (p.nuovo_canone || p.nuovo_consumo) {
      conseguenze.push(abbonate > 0
        ? `Le aziende che lo hanno già nell’abbonamento (${abbonate}) restano alle condizioni di prima finché non le si sposta.`
        : 'Oggi nessuna azienda lo ha nell’abbonamento: il prezzo nuovo vale per tutte da subito.');
      conseguenze.push('I prezzi di prima escono dalla vendita, ma restano validi per chi li ha.');
    } else {
      conseguenze.push('Nessun prezzo nuovo su Stripe: cambiano solo le regole.');
    }
    if (p.tetto_predefinito_eur !== strumento.tetto_predefinito_eur) {
      conseguenze.push(p.tetto_predefinito_eur === null
        ? 'Il tetto di partenza torna pari al canone, per le aziende che non ne hanno scelto uno.'
        : `Il tetto di partenza diventa ${euro(p.tetto_predefinito_eur)} al mese, per le aziende che non ne hanno scelto uno.`);
    }
    if (p.in_vendita !== strumento.in_vendita) {
      conseguenze.push(p.in_vendita ? 'Torna in vendita: le aziende lo possono attivare.' : 'Fuori vendita: chi non ce l’ha non può più attivarlo; chi ce l’ha lo tiene.');
    }
  }
  const cambiaPrezzo = esito.ok && (esito.piano.nuovo_canone || esito.piano.nuovo_consumo);

  const salva = async () => {
    setSalvo(true);
    setErrore(null);
    try {
      const r = await modificaStrumento(richiesta);
      onSalvato(r);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvo(false);
    }
  };

  const campo = { inputProps: { inputMode: 'decimal' as const }, size: 'small' as const, fullWidth: true };
  return (
    <Dialog open={aperto} onClose={salvo ? undefined : onChiudi} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600 }}>{strumento.etichetta}: prezzi e regole</DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
          <TextField {...campo} label="Canone al mese" value={canone} onChange={(e) => setCanone(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
          {!senzaConsumo && (
            <>
              <TextField {...campo} label="Quota compresa" value={quota} onChange={(e) => setQuota(e.target.value)}
                inputProps={{ inputMode: 'numeric' }} InputProps={{ endAdornment: <InputAdornment position="end">{unita(u, 2)}</InputAdornment> }} />
              <TextField {...campo} label={`Extra ${alUnita(u)}`} value={extra} onChange={(e) => setExtra(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
            </>
          )}
        </Stack>
        {senzaConsumo && (
          <Typography variant="body2" sx={{ mt: 1, color: tokens.color.ash }}>Si paga solo col canone: nessun consumo da contare, niente extra né tetto di spesa.</Typography>
        )}

        {!senzaConsumo && (
          <TextField
            {...campo}
            sx={{ mt: 2 }}
            label="Tetto di spesa di partenza"
            placeholder="pari al canone"
            helperText="Quello che vale per le aziende che non ne scelgono uno. Vuoto = pari al canone."
            value={tettoPartenza}
            onChange={(e) => setTettoPartenza(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">€ al mese</InputAdornment> }}
          />
        )}

        <FormControlLabel sx={{ mt: 2 }} control={<Switch checked={inVendita} onChange={(e) => setInVendita(e.target.checked)} />} label="In vendita" />

        {!senzaConsumo && (
          <>
            <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600, color: tokens.color.graphite }}>Al tetto di spesa dell’azienda</Typography>
            <RadioGroup value={alTetto} onChange={(e) => setAlTetto(e.target.value as ComportamentoAlTetto)}>
              {(Object.keys(AL_TETTO) as ComportamentoAlTetto[]).map((k) => (
                <FormControlLabel key={k} value={k} control={<Radio size="small" />} label={<Typography variant="body2">{AL_TETTO[k]}</Typography>} />
              ))}
            </RadioGroup>
          </>
        )}

        <Box sx={{ mt: 2, p: 2, bgcolor: tokens.color.bone, border: `1px solid ${tokens.color.line}`, borderRadius: `${tokens.radius.md}px` }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Cosa succede</Typography>
          {esito.ok
            ? conseguenze.map((c) => <Typography key={c} variant="body2" sx={{ color: tokens.color.ash, mt: 0.5 }}>{c}</Typography>)
            : esito.errore === 'niente_da_cambiare'
              ? <Typography variant="body2" sx={{ color: tokens.color.ash }}>Cambia un valore: qui compare cosa succederà.</Typography>
              : <Typography variant="body2" sx={{ color: tokens.color.danger }}>{ERRORI[esito.errore]}</Typography>}
        </Box>
        {errore && <Alert severity="error" sx={{ mt: 2 }}>{errore}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onChiudi} disabled={salvo}>Annulla</Button>
        <Button variant="contained" disableElevation onClick={() => { void salva(); }} disabled={salvo || !esito.ok}
          startIcon={salvo ? <CircularProgress size={16} color="inherit" /> : undefined}>
          {cambiaPrezzo ? 'Crea i prezzi nuovi' : 'Salva le regole'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
